/** Shared Mermaid theming utilities — reads CSS design tokens and returns theme variables. */

const MOBILE_BREAKPOINT = 640;

function getCssVar(name: string, source?: Element | null): string {
  const scopedValue = source ? getComputedStyle(source).getPropertyValue(name).trim() : '';
  if (scopedValue) return scopedValue;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/**
 * Adapt flowchart direction for the current viewport.
 * Mobile (< 640px): forces TB (top-to-bottom) for vertical stacking.
 * Desktop (>= 640px): forces LR (left-to-right) to fill width.
 * Works regardless of what direction the diagram was authored in.
 */
export function adaptDiagramDirection(syntax: string): string {
  if (typeof window === 'undefined') return syntax;
  const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
  if (isMobile) {
    return syntax.replace(/^(flowchart|graph)\s+(LR|RL)\b/m, '$1 TB');
  }
  return syntax.replace(/^(flowchart|graph)\s+(TB|TD|BT)\b/m, '$1 LR');
}

/** Build a Mermaid `themeVariables` object from the current CSS design tokens. */
export function buildMermaidThemeVariables(source?: Element | null): Record<string, string> {
  return {
    primaryColor: getCssVar('--f-bg-raised', source) || '#f8fafc',
    primaryTextColor: getCssVar('--f-fg', source) || '#0f172a',
    primaryBorderColor: getCssVar('--f-border-strong', source) || '#94a3b8',
    secondaryColor: getCssVar('--f-bg-sunken', source) || '#f1f5f9',
    secondaryTextColor: getCssVar('--f-fg', source) || '#0f172a',
    tertiaryColor: getCssVar('--f-bg-sunken', source) || '#f1f5f9',
    lineColor: getCssVar('--f-border-strong', source) || '#94a3b8',
    textColor: getCssVar('--f-fg', source) || '#0f172a',
    mainBkg: getCssVar('--f-bg-raised', source) || '#f8fafc',
    nodeBorder: getCssVar('--f-border', source) || '#cbd5e1',
    edgeLabelBackground: getCssVar('--f-bg', source) || '#fff',
    fontSize: '16px',
    fontFamily: getCssVar('--f-font-sans', source) || 'Plus Jakarta Sans, sans-serif',
  };
}
